import { useEffect, useState } from "react";
import { api, apiError, getSessionTokenForTest } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, Calendar } from "lucide-react";
import { fmtDateDMY } from "@/lib/format";

/**
 * Legacy Excel export + bank-holiday reference view.
 *
 * The operator worked in a single AppSheet spreadsheet for years. During the
 * cutover we let them keep filling that same sheet — this page emits an .xlsx
 * that mirrors the legacy column layout for any date range.
 */
export default function LegacyExport() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [holidays, setHolidays] = useState([]);

  useEffect(() => {
    api
      .get("/holidays")
      .then((r) => setHolidays(r.data || []))
      .catch(() => {});
  }, []);

  const download = async () => {
    setBusy(true);
    try {
      const url = new URL(
        `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/exports/legacy.xlsx`,
      );
      if (from) url.searchParams.set("date_from", from);
      if (to) url.searchParams.set("date_to", to);
      const token = getSessionTokenForTest();
      const res = await fetch(url.toString(), {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `vas_pos2_legacy_${from || "all"}_${to || "all"}.xlsx`;
      link.click();
      toast.success("Excel downloaded ✓");
    } catch (e) {
      toast.error(apiError(e) || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  const bankHolidays = holidays.filter((h) => h.kind !== "weekly_off");

  return (
    <div className="space-y-6" data-testid="legacy-export-root">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
          Legacy Excel Export
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Download the full multi-sheet workbook (shift entries, readings, payments, debtors,
          lubricants, adjustments, flags, manager daily) matching the old AppSheet column layout.
          Leave dates blank to export everything.
        </p>
      </div>

      <Card className="p-4 border-slate-300">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <Label htmlFor="from">From (optional)</Label>
            <Input
              id="from"
              data-testid="export-date-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="border-slate-300"
            />
          </div>
          <div>
            <Label htmlFor="to">To (optional)</Label>
            <Input
              id="to"
              data-testid="export-date-to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="border-slate-300"
            />
          </div>
          <Button
            data-testid="export-download-btn"
            onClick={download}
            disabled={busy}
            className="bg-slate-900 hover:bg-slate-800 text-white"
          >
            <Download size={16} className="mr-1" /> {busy ? "Preparing…" : "Download Excel"}
          </Button>
        </div>
        <div className="text-xs text-slate-500 mt-3">
          Tip: paste this file directly onto the same tab in your existing sheet so all
          formulas / pivot tables keep working.
        </div>
      </Card>

      <Card className="p-4 border-slate-300">
        <div className="flex items-center gap-2 mb-3">
          <Calendar size={16} className="text-slate-700" />
          <h2 className="text-base font-semibold text-slate-900">
            2026 Bank Holidays ({bankHolidays.length})
          </h2>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Deposit-late SLA is automatically doubled on these days to avoid false flags. Weekly
          offs are excluded from this list.
        </p>
        <div className="overflow-auto">
          <table className="w-full text-sm border border-slate-300">
            <thead className="bg-slate-100">
              <tr>
                <th className="text-left px-2 py-1 border-b border-slate-300">Date</th>
                <th className="text-left px-2 py-1 border-b border-slate-300">Day</th>
                <th className="text-left px-2 py-1 border-b border-slate-300">Holiday</th>
                <th className="text-left px-2 py-1 border-b border-slate-300">Type</th>
              </tr>
            </thead>
            <tbody>
              {bankHolidays.map((h) => (
                <tr key={h.date} className="border-b border-slate-200">
                  <td className="px-2 py-1 font-medium">{fmtDateDMY(h.date)}</td>
                  <td className="px-2 py-1 text-slate-700">{h.day}</td>
                  <td className="px-2 py-1">{h.name}</td>
                  <td className="px-2 py-1">
                    <span className="text-xs px-2 py-0.5 border border-amber-300 bg-amber-50 text-amber-800 rounded-sm">
                      {h.kind}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
